import { Component, Prop, h, Element } from '@stencil/core';

const COMPONENT_NAME = 'me-section';

type SpacingSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';

const DEFAULTS = {
  yGap: '5xl' as SpacingSize,
  yGapLg: '7xl' as SpacingSize,
} as const;

/**
 * A flexible section wrapper component.
 *
 * Renders a semantic HTML tag (e.g. `section`, `main`, `header`) and optionally wraps its contents
 * in a `<me-container>`, with configurable vertical spacing.
 *
 * Passes through additional attributes such as `id`, `aria-*`, and `data-*` to the rendered tag.
 */
@Component({
  tag: 'me-section',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Section {
  /** Reference to the host element (<me-section>) */
  @Element() host: HTMLElement;

  /**
   * The semantic HTML tag to render as the root of the component.
   * Defaults to `'section'`.
   */
  @Prop() tag: 'div' | 'main' | 'footer' | 'header' | 'article' | 'section' = 'section';

  /**
   * If true, disables the inner `<me-container>` wrapper.
   * Use this when you want full control over the layout without extra padding.
   */
  @Prop() noContainer: boolean = false;

  /**
   * Defines the vertical spacing for the inner `<me-container>`.
   * Only applies when `noContainer` is false.
   */
  @Prop() yGap: SpacingSize = DEFAULTS.yGap;
  @Prop() yGapLg: SpacingSize = DEFAULTS.yGapLg;

  /**
   * Defines the horizontal spacing (padding) for the inner `<me-container>`.
   * `xGap` applies at all breakpoints, `xGapMd` at md (768px+), `xGapLg` at lg (1312px+).
   * Only applies when `noContainer` is false.
   */
  @Prop() xGap: SpacingSize | undefined;
  @Prop() xGapMd: SpacingSize | undefined;
  @Prop() xGapLg: SpacingSize | undefined;

  /**
   * Merges the default component class with any custom class
   * provided on the host element (`<me-section class="...">`).
   */
  private getMergedClasses(): string {
    const hostClass = this.host.getAttribute('class') ?? '';
    return `${COMPONENT_NAME} ${hostClass}`.trim();
  }

  render() {
    const Tag = this.tag;
    const className = this.getMergedClasses();

    return (
      <Tag class={className}>
        {this.noContainer ? (
          <slot />
        ) : (
          <me-container yGap={this.yGap} yGapLg={this.yGapLg} xGap={this.xGap} xGapMd={this.xGapMd} xGapLg={this.xGapLg}>
            <slot />
          </me-container>
        )}
      </Tag>
    );
  }
}
